import React, { FC, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { FormModal } from 'transfix-ui/components/Modals'
import { Typography } from 'transfix-ui/core'
import { IFullStop } from 'types/shipment'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ILoad, IStop } from 'apps/fleet-planner/types'
import { updateLoadStatus } from 'apps/fleet-planner/utils/actions/loadDetails'
import * as Apis from 'apps/fleet-planner/utils/api'
import { areRequiredStopsValid } from 'apps/fleet-planner/utils/checkinCheckoutTimesHelpers'
import { getStopNumbers } from 'apps/fleet-planner/utils/stopNumbers'
import { CheckinCheckoutTimesStopContainer } from './CheckinCheckoutTimesStopContainer'

// TODO: Move into a custom hook
const useQueryString = () => {
  const location = useLocation()
  return new URLSearchParams(location.search)
}

interface ICheckinCheckOutTimesModalProps {
  load: ILoad
  isOpen: boolean
  onClose: () => void
}

export const CheckinCheckOutTimesModal: FC<ICheckinCheckOutTimesModalProps> = ({
  isOpen,
  load,
  onClose,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const queryString = useQueryString()
  const newStatus = queryString.get('status') || ''
  const newStopSequence = queryString.get('stopSequence') || ''
  const { getMarketplaceLoad, shipmentDetails } = useMarketplaceContext()
  const { stops: marketplaceStops = [] } = shipmentDetails || {}
  const { stops: loadStops = [], status: currentStatus, externalId } = load || {}
  const initialState = [...loadStops]
  const [stops, setStops] = useState<IStop[]>(initialState)
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(false)

  const dispatch = useDispatch()

  const stopNumbers = useMemo(() => getStopNumbers(stops), [stops.length])
  const submitHandler = async () => {
    const isFormValid = areRequiredStopsValid(stops, newStatus as LOAD_STATE, newStopSequence)
    if (!isFormValid) {
      return setError('Please provide & validate all timestamps.')
    }
    trackEvent('Load', 'DISPATCHER_IN_OUT_FORM_SUBMIT')
    const isCheckInChanged = stops.some(stop => {
      const { checkedInAt, checkedOutAt, startAt } = stop || {}
      const checkInChanged = checkedInAt && checkedInAt !== startAt
      const checkOutChanged = checkedOutAt && checkedOutAt !== startAt
      return checkInChanged || checkOutChanged
    })
    if (isCheckInChanged) {
      trackEvent('Load', 'DISPATCHER_IN_OUT_VALUES_CHANGED')
    }
    try {
      setLoading(true)
      setError(undefined)
      // Dispatch shipment if its in Ready for Dispatch status
      if (currentStatus === LOAD_STATE['Ready for Dispatch']) {
        const [firstPickup] = stops || []
        const { checkedInAt, startAt } = firstPickup || {}
        const pickupEta = checkedInAt || startAt

        dispatch(updateLoadStatus(load, LOAD_STATE.Dispatched, newStopSequence, pickupEta))
      }

      await Apis.updateStops(stops, load)
      dispatch(updateLoadStatus(load, newStatus as LOAD_STATE, newStopSequence))

      if (externalId) getMarketplaceLoad(externalId)

      setLoading(false)
      return onClose()
    } catch (e) {
      setLoading(false)
      return setError(e)
    }
  }

  return (
    <FormModal
      error={error}
      size={5}
      title='Provide missing timestamps to continue'
      open={isOpen}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
        },
        {
          label: loading ? 'Updating...' : 'Submit',
          type: 'confirm',
          action: submitHandler,
          disabled: loading,
        },
      ]}>
      <Typography variant='body2' component='div'>
        Before you provide a status update, please enter any missing check-in or check-out times
        below.
      </Typography>
      {stops.map((stop, idx) => {
        const externalStopId = stop?.externalId ? parseInt(stop?.externalId, 10) : undefined
        const marketplaceStop = marketplaceStops.find(
          (mpStop: IFullStop) => mpStop?.id === externalStopId
        )
        return (
          <CheckinCheckoutTimesStopContainer
            marketplaceStop={marketplaceStop}
            newStatus={newStatus as LOAD_STATE}
            newStopSequence={newStopSequence}
            key={stop.id}
            handleStopChange={setStops}
            stop={stop}
            stops={stops}
            sequence={stopNumbers[idx]}
          />
        )
      })}
    </FormModal>
  )
}
